import moment from "moment";
import { Nav } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "@mui/icons-material";

import excelExport from "../../utils/excelExport";
import Overall from "../../components/Transaction/Overall";
import SuperAgentBilling from "../../components/Transaction/SuperAgentBilling";
import { useDispatch, useSelector } from "react-redux";
import { getBillings } from "../../redux2/billing/billingSlice";
import Loading from "../../components/Loading";
import ChangeExchangeRate from "../../components/ExchangeRate/changeExchangeRate";
import CBEBankListData from "./components/BankList";
import CollapsibleStatSection from "./components/CollapsibleStatSection";
import GridStatGroup from "./components/GridStatGroup";
import { getBillingStatGroups } from "./billingStatsConfig";
import { SUPER_ROLE, USER_ROLE_EMPLOYEE } from "../../common/constants";
import { formatNumber } from "../../utils/convertToNumber";
import DiscountModal from "./Modals/DiscountModal";
import ButtonPrimary from "../../components/Button/ButtonPrimary";
import STCreditModal from "./Modals/STCreditModal";
import { getSuperBillingReport } from "../../services/report";
import Select from "react-select";
import { getCompanies } from "../../redux2/user/userSlice";
import FinancialDashboardModal from "../../components/Modals/FinancialDashboardModal";
import { getTransactions } from "../../redux2/transaction/transactionSlice";
// import KachaLoanList from "./components/KachaLoanList";

const BillingList = ({ query }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { billings, isLoading } = useSelector((state) => state.billing);
  const { user } = useSelector((state) => state.auth);
  const { companies, isLoading: companiesLoading } = useSelector(
    (state) => state.user,
  );
  const { transactions } = useSelector((state) => state.transaction);
  const [current, setCurrent] = useState("Overall Billing");
  const [openExchange, setOpenExchange] = useState(false);
  const [openSTCredit, setOpenSTCredit] = useState(false);
  const [openDiscount, setOpenDiscount] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [bank, setBank] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showBankStat, setShowBankStat] = useState(false);

  const dispatch = useDispatch();

  const NavData = [
    { name: "Overall Billing" },
    { name: "Super Agent Billing" },
  ];

  const bankOptions = [
    { value: "", label: "All Banks" },
    { value: "wallet", label: "Wallet" },
    { value: "cbe_mobile", label: "CBE Mobile Banking" },
    { value: "cbe", label: "CBE" },
    { value: "awash", label: "Awash" },
    { value: "bunna", label: "Bunna" },
    { value: "wegagen", label: "Wegagen" },
    { value: "abyssinia", label: "Abyssinia" },
    { value: "amhara", label: "Amhara" },
    { value: "dashen", label: "Dashen" },
    { value: "zemen", label: "Zemen" },
    { value: "oromia", label: "Oromia" },
    { value: "coop", label: "Coop" },
    { value: "hibret", label: "Hibret" },
    { value: "short-term-credit", label: "Short Term Credit" },
  ];

  const selectedBank =
    bankOptions.find((o) => o.value === (bank || "")) || bankOptions[0];

  query.page = currentPage || 1;
  query.limit = 10;

  const cbeBankAmount = billings?.CBEMobileTransaction?.reduce(
    (acc, { _id, amount }) => {
      acc[_id] = parseFloat(amount).toFixed(2);
      return acc;
    },
    {},
  );

  const awashBankAmount = billings?.AWASHMobileTransaction?.reduce(
    (acc, { _id, amount }) => {
      acc[_id] = parseFloat(amount).toFixed(2);
      return acc;
    },
    {},
  );

  const statGroups = useMemo(() => getBillingStatGroups(billings), [billings]);

  const fullWidthGroups = useMemo(
    () => statGroups.filter((g) => !g.gridGroup),
    [statGroups],
  );

  const revenueGroup = useMemo(
    () => statGroups.filter((g) => g.gridGroup === "revenue"),
    [statGroups],
  );

  const channelsGroup = useMemo(
    () => statGroups.filter((g) => g.gridGroup === "channels"),
    [statGroups],
  );

  const operationsGroup = useMemo(() => {
    const ops = statGroups.filter((g) => g.gridGroup === "operations");
    return [
      ...ops,
      {
        id: "cbe-bank",
        title: "CBE Bank Transaction Stat",
        subtitle: "Mobile banking bookings, deposits & credit settlements",
        stats: CBEBankListData(cbeBankAmount, formatNumber),
        defaultOpen: false,
        variant: "default",
        badge: 4,
      },
      {
        id: "awash-bank",
        title: "Awash Webirr Transaction Stat",
        subtitle: "Webirr Awash bookings, deposits & credit settlements",
        stats: CBEBankListData(awashBankAmount, formatNumber, "awash"),
        defaultOpen: false,
        variant: "default",
        badge: 4,
      },
    ];
  }, [statGroups, cbeBankAmount, awashBankAmount]);

  const renderTransaction = () => {
    switch (current) {
      case "Overall Billing":
        return (
          <Overall
            billings={billings}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        );
      case "Super Agent Billing":
        return <SuperAgentBilling billings={billings?.superBillings} />;
      default:
        return (
          <Overall
            billings={billings}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        );
    }
  };

  async function handleExport() {
    setLoader(true);
    query.search = search || "";
    query.bank = bank || "";
    query.companyId = selectedCompany?.value || "";

    const billReport = await getSuperBillingReport(query);

    let fileName = "Billing Report " + moment().format("DD-MM-YYYY");

    const agentBilling = billReport?.billings?.filter((item) => item.company);

    const getNetRevenue = (billing) => {
      if (user?.role === SUPER_ROLE) {
        return billing?.amount + billing?.serviceCharge - billing?.systemFee;
      }
      return 0;
    };

    let data = agentBilling?.map((billing, index) => {
      return {
        "S/N": index + 1,
        Status:
          billing?.type === "refundDeposit"
            ? "Refund Deposit"
            : billing?.type === "voidDeposit"
              ? "Void Deposit"
              : billing?.type === "STCreditSettlement"
                ? "Short-term Payment"
                : billing?.reason
                  ? "Credit Payment"
                  : billing?.deposit
                    ? "Wallet Deposit"
                    : billing?.status === "refunded"
                      ? "Refunded"
                      : billing?.status === "voided"
                        ? "Void"
                        : billing?.status === "void-late"
                          ? "Void-Late"
                          : billing?.status === "exchanged"
                            ? "Exchanged"
                            : billing?.type === "exchangeFee"
                              ? "Exchanged Fee"
                              : billing?.type === "voidFee"
                                ? "Void Fee"
                                : "Issued",
        "Company Name": billing?.company ? billing?.company?.name : "N/A",
        "Transaction ID": billing.transaction ? billing.transaction : "-",
        "Ticket Number": billing?.ticketNumber?.join(", "),
        "Old Ticket Number": billing?.exTicketNumber?.join(", "),
        "Deposit Amount":
          billing?.type === "refundDeposit"
            ? -billing?.deposit?.toFixed(2)
            : +billing?.deposit?.toFixed(2),
        "Total Amount":
          +billing?.amount?.toFixed(2) + +billing?.serviceCharge?.toFixed(2) ||
          0,
        "System Fare": +billing?.amount?.toFixed(2) || 0,
        "Sterling S.C": +billing?.serviceCharge?.toFixed(2) || 0,
        ...(user?.role === SUPER_ROLE && {
          "NDC S.C": +billing?.NDCServiceCharge?.toFixed(2) || 0,
        }),
        "System Fee": +billing?.systemFee?.toFixed(2) || 0,
        ...(user?.role === SUPER_ROLE && {
          "Net Revenue": getNetRevenue(billing) || 0,
        }),
        "Payment Method": billing.bank,
        Penalty: +billing?.penalty?.toFixed(2) || 0,
        Tax: +billing?.tax?.toFixed(2) || 0,
        "Used Fare": +billing?.usedFare?.toFixed(2) || 0,
        "Fare Difference": +billing?.exchangeDifference?.toFixed(2) || 0,
        "Paid By": billing?.paidBy,
        // Balance: billing?.balance
        // ? formatNumber(billing?.balance.toFixed(2))
        // : "0.00",
        "Created At": moment(billing.createdAt).format("MMM DD, YYYY"),
      };
    });
    let fileName2 = "Super Billing Report " + moment().format("DD-MM-YY");
    const superAgentBilling = billReport?.billings?.filter(
      (item) => !item.company,
    );
    const superAgentData = superAgentBilling?.map((billing, index) => {
      return {
        "S/N": index + 1,
        Status:
          billing?.type === "refundDeposit"
            ? "Refund Deposit"
            : billing?.type === "voidDeposit"
              ? "Void Deposit"
              : billing?.type === "STCreditSettlement"
                ? "Short-term Payment"
                : billing?.reason
                  ? "Credit Payment"
                  : billing?.deposit
                    ? "Wallet Deposit"
                    : billing?.status === "refunded"
                      ? "Refunded"
                      : billing?.status === "voided"
                        ? "Void"
                        : billing?.status === "void-late"
                          ? "Void-Late"
                          : billing?.status === "exchanged"
                            ? "Exchanged"
                            : billing?.type === "exchangeFee"
                              ? "Exchanged Fee"
                              : billing?.type === "voidFee"
                                ? "Void Fee"
                                : "Issued",
        "Company Name": "Sterling travels & tour",
        "Transaction ID": billing.transaction ? billing.transaction : "-",
        "Ticket Number": billing?.ticketNumber?.join(", "),
        "Corporate Name": billing?.corporateName || "N/A",
        "Base Amount": +(billing.deposit || billing.amount).toFixed(2),
        "Payment Method": billing.bank,
        "Service Charge": +billing.serviceCharge.toFixed(2) || "0.00",
        "Paid By": billing?.paidBy,
        "Created At": moment(billing.createdAt).format("MMM DD, YYYY"),
      };
    });

    excelExport(data, fileName);
    if (!selectedCompany?.value) {
      excelExport(superAgentData, fileName2);
    }
    setLoader(false);
  }

  const handleSearch = () => {
    setSearch(searchValue);
  };

  useEffect(() => {
    query.search = search || "";
    query.bank = bank || "";
    query.company = selectedCompany?.value || "";

    if (search) {
      const timeOutId = setTimeout(() => dispatch(getBillings(query)), 1000);
      return () => clearTimeout(timeOutId);
    } else {
      dispatch(getBillings(query));
    }
  }, [query, dispatch, currentPage, search, bank, selectedCompany]);

  useEffect(() => {
    dispatch(getCompanies());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getTransactions(query));
  }, [query, dispatch]);

  const companyId = searchParams.get("company");

  const companyOptions = useMemo(() => {
    return (
      [
        { value: "", label: "All", default: true },
        ...companies?.map((company) => ({
          value: company._id,
          label: company.name,
        })),
      ] ?? []
    );
  }, [companies]);

  useEffect(() => {
    if (companyId && companyOptions.length > 0) {
      const match = companyOptions.find((opt) => opt.value === companyId);
      setSelectedCompany(match || null);
    }
  }, [companyId, companyOptions]);

  function handleCompanyChange(selectedOption) {
    if (!selectedOption) return;
    setSelectedCompany(selectedOption);
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      company: selectedOption.value,
    });
  }

  if (isLoading || loader) {
    return <Loading />;
  }

  return (
    <>
      {user?.role !== USER_ROLE_EMPLOYEE && user?.showBilling && (
        <div className="mb-10">
          <div className="space-y-0">
            {fullWidthGroups.map((group) => (
              <CollapsibleStatSection
                key={group.id}
                title={group.title}
                subtitle={group.subtitle}
                stats={group.stats}
                defaultOpen={group.defaultOpen}
                variant={group.variant}
                badge={group.badge}
              />
            ))}
            <GridStatGroup
              groups={revenueGroup}
              className="grid-cols-1 md:grid-cols-2"
            />

            <GridStatGroup groups={channelsGroup} />

            <GridStatGroup groups={operationsGroup} />
          </div>
        </div>
      )}

      <div className="flex-wrap my-4 d-flex justify-content-between align-items-center">
        <div className="mt-3 mb-4 rounded shadow-sm card-tabs mt-sm-0 mb-xxl-0">
          <Nav as="ul" className="nav nav-tabs">
            {NavData.map(({ name }, index) => (
              <Nav.Item
                as="li"
                key={index}
                className="nav-item"
                onClick={() => setCurrent(name)}
              >
                <Nav.Link
                  className="px-4 py-2 text-4xl font-bold nav-link"
                  eventKey={name}
                  active={current === name}
                >
                  {name}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>

        <div className="flex gap-2 items-center mx-2">
          {/* <Dropdown className="dropdown text-sans-serif">
            <Dropdown.Toggle
              // id="dropdown"
              variant=""
              className="p-2 px-4 mx-3 text-white border-main-100 border-1"
              type="button"
              id="order-dropdown-0"
              data-toggle="dropdown"
              data-boundary="viewport"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <div className="flex">
                <h1 className="mx-2">Action</h1>
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    width="18px"
                    height="18px"
                    viewBox="0 0 24 24"
                    version="1.1"
                  >
                    <g
                      stroke="none"
                      strokeWidth={1}
                      fill="none"
                      fillRule="evenodd"
                    >
                      <rect x={0} y={0} width={24} height={24} />
                      <circle fill="#000000" cx={5} cy={12} r={2} />
                      <circle fill="#000000" cx={12} cy={12} r={2} />
                      <circle fill="#000000" cx={19} cy={12} r={2} />
                    </g>
                  </svg>
                </span>
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu
              className="py-0 border dropdown-menu dropdown-menu-right"
              aria-labelledby="order-dropdown-0"
              style={{ minWidth: "20rem" }}
            >
              <div className="px-4 mx-2 mt-2 text-lg font-medium text-black">
                <div className="py-2 hover:text-main">
                  <KachaLoanList />
                </div>
              </div>
            </Dropdown.Menu>
          </Dropdown> */}
          <ButtonPrimary
            className="mb-4 text-black rounded-2xl border-1 border-main bg-slate-50 hover:text-white hover:bg-main-900 focus:outline-none focus:ring-0 mb-xxl-0"
            onClick={() => setOpenSTCredit(true)}
          >
            ST Credit
          </ButtonPrimary>

          <ButtonPrimary
            className="mb-4 text-black rounded-2xl border-1 border-main bg-slate-50 hover:text-white hover:bg-main-900 focus:outline-none focus:ring-0 mb-xxl-0"
            onClick={() => setOpenExchange(true)}
          >
            Exchange Rate
          </ButtonPrimary>

          <ButtonPrimary
            className="mb-4 text-black rounded-2xl border-1 border-main bg-slate-50 hover:text-white hover:bg-main-900 focus:outline-none focus:ring-0 mb-xxl-0"
            onClick={() => setOpenDiscount(true)}
          >
            Discount
          </ButtonPrimary>

          {user?.showBilling && (
            <ButtonPrimary
              className="mb-4 text-black rounded-2xl border-1 border-main bg-slate-50 hover:text-white hover:bg-main-900 focus:outline-none focus:ring-0 mb-xxl-0"
              onClick={() => setShowBankStat(true)}
            >
              Bank Stat
            </ButtonPrimary>
          )}
          <div
            className="mb-4 text-white btn bg-main hover:bg-main-800 border-main-100 mb-xxl-0"
            onClick={handleExport}
          >
            <i className="far fa-file-word me-2"></i>Generate Report
          </div>
        </div>
      </div>

      <div>
        <div className="flex flex-col justify-between items-center md:flex-row">
          <h3 className="text-2xl font-black text-primary">Transaction</h3>
          <div className="flex flex-col gap-2 justify-end px-2 w-full md:flex-row">
            <Select
              placeholder="Filter by Company"
              isLoading={companiesLoading}
              value={selectedCompany}
              options={companyOptions}
              isSearchable={true}
              onChange={handleCompanyChange}
              style={{
                lineHeight: "40px",
                color: "#7e7e7e",
                paddingLeft: " 15px",
              }}
              className="w-full md:w-1/4"
            />
            <Select
              placeholder={bank || "Select Bank"}
              value={bank}
              options={[
                { value: "", label: "All", default: true },
                { value: "wallet", label: "Wallet" },
                { value: "cbe_mobile", label: "CBE Mobile Banking" },
                { value: "cbe", label: "CBE" },
                { value: "awash", label: "Awash" },
                { value: "bunna", label: "Bunna" },
                { value: "wegagen", label: "Wegagen" },
                { value: "abyssinia", label: "Abyssinia" },
                { value: "amhara", label: "Amhara" },
                { value: "dashen", label: "Dashen" },
                { value: "zemen", label: "Zemen" },
                { value: "oromia", label: "Oromia" },
                { value: "coop", label: "Coop" },
                { value: "hibret", label: "Hibret" },
                { value: "short-term-credit", label: "Short Term Credit" },
              ]}
              style={{
                lineHeight: "40px",
                color: "#7e7e7e",
                padding: "10px",
                // paddingLeft: "15px",
              }}
              className="w-full md:w-1/4"
              onChange={(e) => setBank(e.value)}
            />
            <div className="rounded-lg">
              <div className="flex w-full bg-white border-1 border-main">
                <input
                  type="text"
                  className="px-3 py-2.5 w-full"
                  placeholder="Search here"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <span className="mx-2 mt-2">
                  <button onClick={handleSearch}>
                    <Search size={30} className="text-main" />
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
        {renderTransaction()}
      </div>
      <ChangeExchangeRate show={openExchange} setOpen={setOpenExchange} />
      <DiscountModal
        show={openDiscount}
        setOpen={setOpenDiscount}
        companies={companies}
        isLoading={companiesLoading}
      />
      <STCreditModal
        show={openSTCredit}
        setOpen={setOpenSTCredit}
        STCredit={billings?.STCreditAmount}
      />
      <FinancialDashboardModal
        show={showBankStat}
        onHide={() => setShowBankStat(false)}
        financialData={{
          depositByBank: transactions?.allDepositByBank,
          companyTotalSales: transactions?.allTotalSales,
        }}
      />
    </>
  );
};

export default BillingList;
